import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Filter, CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";

interface SearchFiltersProps {
  onFilterChange: (filters: FilterState) => void;
}

export interface FilterState {
  categories: string[];
  dateFrom?: Date;
  dateTo?: Date;
  readTimes: string[];
}

const readTimeOptions = [
  { value: "quick", label: "Quick Read (< 5 min)", max: 5 },
  { value: "medium", label: "Medium (5-10 min)", min: 5, max: 10 },
  { value: "long", label: "Long Read (> 10 min)", min: 10 },
];

export const SearchFilters = ({ onFilterChange }: SearchFiltersProps) => {
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [selectedReadTimes, setSelectedReadTimes] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    onFilterChange({
      categories: selectedCategories,
      dateFrom,
      dateTo,
      readTimes: selectedReadTimes,
    });
  }, [selectedCategories, dateFrom, dateTo, selectedReadTimes]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("id, name, color")
        .order("name");

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleReadTime = (value: string) => {
    setSelectedReadTimes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const clearAllFilters = () => {
    setSelectedCategories([]);
    setDateFrom(undefined);
    setDateTo(undefined);
    setSelectedReadTimes([]);
  };

  const activeFilterCount =
    selectedCategories.length +
    (dateFrom ? 1 : 0) +
    (dateTo ? 1 : 0) +
    selectedReadTimes.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Popover open={showFilters} onOpenChange={setShowFilters}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="gap-2">
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1 px-1.5 py-0.5 text-xs">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0 bg-popover z-50" align="start">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-sm">Filters</h4>
                {activeFilterCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearAllFilters}
                    className="h-auto py-1 px-2 text-xs"
                  >
                    Clear all
                  </Button>
                )}
              </div>
            </div>

            <div className="p-4 space-y-6 max-h-[500px] overflow-y-auto">
              {/* Categories */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium">Categories</h5>
                <div className="space-y-2">
                  {categories.map((category) => (
                    <label
                      key={category.id}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedCategories.includes(category.id)}
                        onCheckedChange={() => toggleCategory(category.id)}
                      />
                      <span className="text-sm">{category.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Date Range */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium">Date Range</h5>
                <div className="grid grid-cols-2 gap-2">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "justify-start text-left font-normal",
                          !dateFrom && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateFrom ? format(dateFrom, "MMM d, yyyy") : "From"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-popover z-50" align="start">
                      <Calendar
                        mode="single"
                        selected={dateFrom}
                        onSelect={setDateFrom}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>

                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "justify-start text-left font-normal",
                          !dateTo && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateTo ? format(dateTo, "MMM d, yyyy") : "To"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-popover z-50" align="start">
                      <Calendar
                        mode="single"
                        selected={dateTo}
                        onSelect={setDateTo}
                        initialFocus
                        className={cn("p-3 pointer-events-auto")}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Read Time */}
              <div className="space-y-3">
                <h5 className="text-sm font-medium">Read Time</h5>
                <div className="space-y-2">
                  {readTimeOptions.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedReadTimes.includes(option.value)}
                        onCheckedChange={() => toggleReadTime(option.value)}
                      />
                      <span className="text-sm">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* Active Filter Tags */}
        {selectedCategories.length > 0 && (
          <>
            {selectedCategories.map((categoryId) => {
              const category = categories.find((c) => c.id === categoryId);
              return (
                <Badge key={categoryId} variant="secondary" className="gap-1">
                  {category?.name}
                  <button
                    onClick={() => toggleCategory(categoryId)}
                    className="ml-1 hover:bg-background/20 rounded-full"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </>
        )}

        {dateFrom && (
          <Badge variant="secondary" className="gap-1">
            From: {format(dateFrom, "MMM d, yyyy")}
            <button
              onClick={() => setDateFrom(undefined)}
              className="ml-1 hover:bg-background/20 rounded-full"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}

        {dateTo && (
          <Badge variant="secondary" className="gap-1">
            To: {format(dateTo, "MMM d, yyyy")}
            <button
              onClick={() => setDateTo(undefined)}
              className="ml-1 hover:bg-background/20 rounded-full"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        )}

        {selectedReadTimes.length > 0 && (
          <>
            {selectedReadTimes.map((value) => {
              const option = readTimeOptions.find((o) => o.value === value);
              return (
                <Badge key={value} variant="secondary" className="gap-1">
                  {option?.label}
                  <button
                    onClick={() => toggleReadTime(value)}
                    className="ml-1 hover:bg-background/20 rounded-full"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};
